import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BarChart3, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { BOOKING_TYPES, money, when } from "@/lib/rides";
import { PageHead } from "@/pages/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const EMPTY = { code: "", description: "", discount_type: "percentage", discount_value: 20, active: true, start_date: "", expiration_date: "",
  usage_limit: "", per_user_limit: "", minimum_fare: 0, minimum_rides: 0, applicable_ride_types: [], applicable_user_types: [], allow_with_recurring_discount: false };
const toLocal = (v) => (v ? new Date(new Date(v).getTime() - new Date(v).getTimezoneOffset() * 60000).toISOString().slice(0, 16) : "");
const Toggle = ({ list, value, onChange, testid }) => (
  <div className="flex flex-wrap gap-1.5">
    {Object.entries(list).map(([k, v]) => (
      <button key={k} type="button" data-testid={`${testid}-${k}`} onClick={() => onChange(value.includes(k) ? value.filter((x) => x !== k) : [...value, k])}
        className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${value.includes(k) ? "border-primary bg-primary text-primary-foreground" : "border-white/15 text-muted-foreground"}`}>{v}</button>
    ))}
  </div>
);
const F = ({ label, children }) => <div className="space-y-1"><Label className="text-xs">{label}</Label>{children}</div>;

function CodeDialog({ initial, onClose }) {
  const qc = useQueryClient();
  const [f, setF] = useState(initial ? { ...EMPTY, ...initial, start_date: toLocal(initial.start_date), expiration_date: toLocal(initial.expiration_date),
    usage_limit: initial.usage_limit ?? "", per_user_limit: initial.per_user_limit ?? "" } : EMPTY);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const save = async (e) => {
    e.preventDefault();
    const n = (v) => (v === "" || v == null ? null : Number(v));
    const body = { ...f, discount_value: Number(f.discount_value), minimum_fare: Number(f.minimum_fare) || 0, minimum_rides: Number(f.minimum_rides) || 0,
      usage_limit: n(f.usage_limit), per_user_limit: n(f.per_user_limit),
      start_date: f.start_date ? new Date(f.start_date).toISOString() : null, expiration_date: f.expiration_date ? new Date(f.expiration_date).toISOString() : null };
    ["id", "times_used", "created_by", "created_at", "updated_at", "deleted"].forEach((k) => delete body[k]);
    try {
      if (initial?.id) await api.put(`/admin/discounts/${initial.id}`, body);
      else await api.post("/admin/discounts", body);
      toast.success("Discount code saved");
      qc.invalidateQueries({ queryKey: ["discounts"] });
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="discount-dialog">
        <DialogHeader><DialogTitle>{initial ? "Edit discount code" : "Create discount code"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid grid-cols-2 gap-3">
          <F label="Code"><Input required value={f.code} onChange={set("code")} className="uppercase" data-testid="discount-form-code" /></F>
          <F label="Type">
            <select value={f.discount_type} onChange={set("discount_type")} className="h-9 w-full rounded-md border bg-background px-2 text-sm" data-testid="discount-form-type">
              <option value="percentage">Percentage</option><option value="fixed">Fixed dollar</option>
            </select>
          </F>
          <div className="col-span-2"><F label="Description"><Input value={f.description} onChange={set("description")} data-testid="discount-form-description" /></F></div>
          <F label={f.discount_type === "percentage" ? "Percent off" : "Dollars off"}><Input required type="number" step="0.01" value={f.discount_value} onChange={set("discount_value")} data-testid="discount-form-value" /></F>
          <F label="Minimum fare ($)"><Input type="number" step="0.01" value={f.minimum_fare} onChange={set("minimum_fare")} data-testid="discount-form-min-fare" /></F>
          <F label="Starts"><Input type="datetime-local" value={f.start_date} onChange={set("start_date")} className="[color-scheme:dark]" data-testid="discount-form-start" /></F>
          <F label="Expires"><Input type="datetime-local" value={f.expiration_date} onChange={set("expiration_date")} className="[color-scheme:dark]" data-testid="discount-form-expires" /></F>
          <F label="Total uses (blank = unlimited)"><Input type="number" value={f.usage_limit} onChange={set("usage_limit")} data-testid="discount-form-usage-limit" /></F>
          <F label="Uses per rider"><Input type="number" value={f.per_user_limit} onChange={set("per_user_limit")} data-testid="discount-form-per-user" /></F>
          <F label="Minimum completed rides"><Input type="number" value={f.minimum_rides} onChange={set("minimum_rides")} data-testid="discount-form-min-rides" /></F>
          <div className="col-span-2"><F label="Ride types (none = all)"><Toggle list={BOOKING_TYPES} value={f.applicable_ride_types} onChange={set("applicable_ride_types")} testid="discount-form-ride-type" /></F></div>
          <div className="col-span-2"><F label="Riders (none = all)"><Toggle list={{ new: "New riders", returning: "Returning riders" }} value={f.applicable_user_types} onChange={set("applicable_user_types")} testid="discount-form-user-type" /></F></div>
          <label className="col-span-2 flex items-center justify-between text-sm">Can stack with the recurring discount
            <Switch checked={f.allow_with_recurring_discount} onCheckedChange={set("allow_with_recurring_discount")} data-testid="discount-form-stack" /></label>
          <label className="col-span-2 flex items-center justify-between text-sm">Active <Switch checked={f.active} onCheckedChange={set("active")} data-testid="discount-form-active" /></label>
          <Button type="submit" className="col-span-2" data-testid="discount-form-save">Save code</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Usage({ code, onClose }) {
  const { data } = useQuery({ queryKey: ["discountUsage", code.id], queryFn: () => api.get(`/admin/discounts/${code.id}/usage`).then((r) => r.data) });
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="discount-usage-dialog">
        <DialogHeader><DialogTitle>{code.code} usage</DialogTitle></DialogHeader>
        {!data?.length ? <p className="text-sm text-muted-foreground">Not used yet.</p> : data.map((u) => (
          <div key={u.id} className="flex justify-between border-b border-white/5 py-1.5 text-sm" data-testid="discount-usage-row">
            <span>{u.rider_name} · {when(u.created_at)} · {u.status}</span><span className="font-semibold">-{money(u.amount)}</span>
          </div>
        ))}
      </DialogContent>
    </Dialog>
  );
}

export default function AdminDiscounts() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["discounts"], queryFn: () => api.get("/admin/discounts").then((r) => r.data) });
  const [editing, setEditing] = useState(null);
  const [usage, setUsage] = useState(null);
  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); qc.invalidateQueries({ queryKey: ["discounts"] }); } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-5">
      <PageHead title="Discount codes" sub="Validated and calculated by the server. Codes apply to the first ride of a booking. Booking fee is always $0.">
        <Button onClick={() => setEditing({})} data-testid="discount-create-button"><Plus className="mr-1 h-4 w-4" />Create code</Button>
      </PageHead>
      <div className="grid gap-3 md:grid-cols-2">
        {data?.map((c) => (
          <div key={c.id} className={`rounded-xl border bg-card p-4 ${c.active ? "" : "opacity-60"}`} data-testid={`discount-row-${c.code}`}>
            <div className="flex items-start justify-between gap-2">
              <div><p className="font-display text-lg font-bold">{c.code}</p><p className="text-xs text-muted-foreground">{c.description}</p></div>
              <span className="text-lg font-bold text-neon-lime">{c.discount_type === "percentage" ? `${c.discount_value}%` : money(c.discount_value)} off</span>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              {c.active ? "Active" : "Inactive"} · used {c.times_used || 0}{c.usage_limit ? `/${c.usage_limit}` : ""}{c.expiration_date ? ` · expires ${when(c.expiration_date)}` : ""}
              {c.minimum_fare ? ` · min ${money(c.minimum_fare)}` : ""}{c.allow_with_recurring_discount ? " · stacks with recurring" : ""}
            </p>
            <div className="mt-3 flex flex-wrap gap-1.5">
              <Button size="sm" variant="outline" onClick={() => setEditing(c)} data-testid={`discount-edit-${c.code}`}>Edit</Button>
              <Button size="sm" variant="ghost" onClick={() => setUsage(c)} data-testid={`discount-usage-${c.code}`}><BarChart3 className="mr-1 h-3.5 w-3.5" />Usage</Button>
              {c.active && <Button size="sm" variant="ghost" onClick={() => act(() => api.post(`/admin/discounts/${c.id}/deactivate`), "Code deactivated")} data-testid={`discount-deactivate-${c.code}`}>Deactivate</Button>}
              <Button size="sm" variant="ghost" className="text-destructive" onClick={() => window.confirm(`Delete ${c.code}?`) && act(() => api.delete(`/admin/discounts/${c.id}`), "Code deleted")} data-testid={`discount-delete-${c.code}`}>Delete</Button>
            </div>
          </div>
        ))}
        {data && !data.length && <p className="text-sm text-muted-foreground">No discount codes yet.</p>}
      </div>
      {editing && <CodeDialog initial={editing.id ? editing : null} onClose={() => setEditing(null)} />}
      {usage && <Usage code={usage} onClose={() => setUsage(null)} />}
    </div>
  );
}
